import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import {
  LoadToEdgeData,
  LoadToVectorData,
  LoadToVertexData,
  LoadingJobData,
  OneColumnMappingDataSourceColumnSource,
  OneColumnMappingGraphEntityTarget,
  OneColumnMappingMappingWidgetSource,
  OneColumnMappingMappingWidgetTarget,
} from '@tigergraph/tools-models/loading-job';
import { Attribute } from '@tigergraph/tools-models/topology';
import { ValidateResult } from '@tigergraph/tools-models/utils';
import { ParagraphSmall } from 'baseui/typography';
import { EmbeddingAttribute } from '@tigergraph/tools-models/topology/embedding-attribute.model';
import AttributeEditor from '@/components/schemaDesigner/attributeEditorContainer/attributeEditor/AttributeEditor';
import EmbeddingAttributeEditor from '@/components/schemaDesigner/attributeEditorContainer/attributeEditor/EmbeddingAttributeEditor';
import { useWorkspaceContext } from '@/contexts/workspaceContext';
import { compare } from 'compare-versions';

export interface AttributeEditorContainerProps {
  isVertex: boolean;
  primaryAttrs: Attribute[];
  attributes: Attribute[];
  embeddingAttributes?: EmbeddingAttribute[];
  validateResult: ValidateResult | undefined;
  mappingValidateResult?: ValidateResult | undefined;
  loadingJob?: LoadingJobData;
  loadingStatement?: LoadToVertexData | LoadToEdgeData;
  embeddingLoadingStatements?: LoadToVectorData[];
  readOnly?: boolean;
  maxHeight?: string;
  onSave: (attributes: Attribute[], embeddingAttributes?: EmbeddingAttribute[]) => void;
  onDeleteLoadingStatementMappings?: (indices: number[]) => void;
  onChangeLoadingStatementMapping?: (
    mapping: {
      mappingSource: OneColumnMappingDataSourceColumnSource | OneColumnMappingMappingWidgetSource;
      mappingTarget: OneColumnMappingGraphEntityTarget | OneColumnMappingMappingWidgetTarget;
    }[]
  ) => void;
  onDeleteVectorLoadingStatements?: (vectorNames: string[]) => void;
  onChangeVectorLoadingStatementMapping?: (
    vectorName: string,
    loadingStatement: LoadToVectorData,
    mappings?: {
      mappingSource: OneColumnMappingDataSourceColumnSource | OneColumnMappingMappingWidgetSource;
      mappingTarget: OneColumnMappingGraphEntityTarget | OneColumnMappingMappingWidgetTarget;
    }[]
  ) => void;
}

export default function AttributeEditorContainer(props: AttributeEditorContainerProps) {
  const [css, theme] = useStyletron();
  const {
    isVertex,
    primaryAttrs,
    attributes,
    embeddingAttributes,
    loadingJob,
    loadingStatement,
    embeddingLoadingStatements,
    readOnly,
    maxHeight,
    validateResult,
    mappingValidateResult,
    onSave,
    onDeleteLoadingStatementMappings,
    onChangeLoadingStatementMapping,
    onDeleteVectorLoadingStatements,
    onChangeVectorLoadingStatementMapping,
  } = props;
  const { currentWorkspace } = useWorkspaceContext();

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        rowGap: '8px',
        minHeight: '180px',
      })}
    >
      <AttributeEditor
        isVertex={isVertex}
        primaryAttrs={primaryAttrs}
        attributes={attributes}
        validateResult={validateResult}
        mappingValidateResult={mappingValidateResult}
        loadingJob={loadingJob}
        loadingStatement={loadingStatement}
        readOnly={readOnly}
        maxHeight={maxHeight}
        onSave={(attributes) => onSave(attributes, embeddingAttributes)}
        onDeleteLoadingStatementMappings={onDeleteLoadingStatementMappings}
        onChangeLoadingStatementMapping={onChangeLoadingStatementMapping}
      />
      {isVertex &&
        embeddingAttributes &&
        ((currentWorkspace?.tg_version && compare(currentWorkspace?.tg_version, '4.1.x', '>')) ||
          embeddingAttributes.length > 0) && (
          <EmbeddingAttributeEditor
            attributes={embeddingAttributes}
            validateResult={validateResult}
            loadingJob={loadingJob}
            loadingStatements={embeddingLoadingStatements}
            readOnly={readOnly}
            onSave={(embeddingAttributes) => onSave(attributes, embeddingAttributes)}
            onDeleteLoadingStatements={onDeleteVectorLoadingStatements}
            onChangeLoadingStatementMapping={onChangeVectorLoadingStatementMapping}
          />
        )}
      <ParagraphSmall
        className={css({
          textAlign: 'center',
          color: theme.colors['text.secondary'],
          marginBottom: '8px',
        })}
      >
        Click &quot;+&quot; to add attributes.
      </ParagraphSmall>
    </div>
  );
}
